import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon, Flex
} from "@chakra-ui/react";
import { FaTint, FaHome, FaArrowRight, FaCheckCircle } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

const WaterFiltersHub: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/water-filters"
        fallbackTitle="Water Filters for Australian Homes — One Water Australia"
        fallbackDescription="From under-sink to whole-home — find the right water filter for your Australian home. Professional specification and installation."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 16, md: 24 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1584467541268-b040f83be3fd?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center" maxW="3xl" mx="auto">
            <Icon as={FaTint} boxSize={12} color={WATER_BLUE} />
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              Water filters for Australian homes.
            </Heading>
            <Text fontSize="xl" color="whiteAlpha.800">
              From under-sink to whole-home — find the right filter for your water.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={WATER_BLUE}
              color={NAVY}
              size="lg"
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#8DC5E0" }}
              transition="all 0.2s"
            >
              Book a Free Water Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Filter types */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={4} mb={14} textAlign="center">
          <Heading size="xl" color={NAVY}>Types of Water Filters</Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            The best filter starts with understanding your water. Here's what's available — and who each option suits.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          <Box
            bg={ICE_MIST}
            borderRadius="xl"
            border={`2px solid ${WATER_BLUE}40`}
            _hover={{ shadow: "lg", borderColor: WATER_BLUE }}
            transition="all 0.3s"
            overflow="hidden"
          >
            <Box h="220px" overflow="hidden">
              <Box
                as="img"
                src="https://images.unsplash.com/photo-1585771724684-38269d6639fd?auto=format&fit=crop&w=800&q=80"
                alt="Under-sink water filter installation in Australian home"
                w="full"
                h="full"
                objectFit="cover"
              />
            </Box>
            <Box p={10}>
              <Icon as={FaTint} boxSize={10} color={DEEP_AQUA} mb={4} />
              <Heading size="lg" color={NAVY} mb={3}>Under-Sink Water Filter</Heading>
              <Text color="gray.700" mb={6}>
                Installed beneath your kitchen sink, an under-sink filter treats all water from that tap — drinking, cooking, ice. The most common upgrade from a jug filter. Professionally specified for your supply.
              </Text>
              <VStack align="flex-start" spacing={2} mb={6}>
                {["Better taste and no chlorine odour", "Filters cooking and beverage water", "No countertop clutter", "Professional installation available"].map((b) => (
                  <HStack key={b} spacing={2}>
                    <Icon as={FaCheckCircle} color={DEEP_AQUA} boxSize={4} />
                    <Text fontSize="sm" color="gray.700">{b}</Text>
                  </HStack>
                ))}
              </VStack>
              <Button
                as={RouterLink}
                to="/water/water-filters/under-sink-system"
                bg={NAVY}
                color="white"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: DEEP_AQUA }}
                transition="all 0.2s"
              >
                Under-Sink Water Filter
              </Button>
            </Box>
          </Box>

          <Box
            bg="white"
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.200"
            _hover={{ shadow: "lg", borderColor: WATER_BLUE }}
            transition="all 0.3s"
            overflow="hidden"
          >
            <Box h="220px" overflow="hidden">
              <Box
                as="img"
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800&q=80"
                alt="Modern Australian home with whole-home water filtration"
                w="full"
                h="full"
                objectFit="cover"
              />
            </Box>
            <Box p={10}>
              <Icon as={FaHome} boxSize={10} color={DEEP_AQUA} mb={4} />
              <Heading size="lg" color={NAVY} mb={3}>Whole-Home Water Filter</Heading>
              <Text color="gray.700" mb={6}>
                Installed at your home's main water entry point, a whole-home filter treats every tap, shower, and appliance. Addresses chlorine, sediment, and taste at the source — not just the kitchen.
              </Text>
              <VStack align="flex-start" spacing={2} mb={6}>
                {["Every tap filtered", "Reduces chlorine for skin and hair", "Protects appliances from sediment", "Reduces chlorine odour throughout the home"].map((b) => (
                  <HStack key={b} spacing={2}>
                    <Icon as={FaCheckCircle} color={DEEP_AQUA} boxSize={4} />
                    <Text fontSize="sm" color="gray.700">{b}</Text>
                  </HStack>
                ))}
              </VStack>
              <Button
                variant="outline"
                borderColor={NAVY}
                color={NAVY}
                rightIcon={<FaArrowRight />}
                _hover={{ bg: NAVY, color: "white" }}
                transition="all 0.2s"
              >
                Whole-Home Filter
              </Button>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>

      {/* How to choose */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
            <VStack align="flex-start" spacing={6}>
              <Heading size="xl" color={NAVY}>How to choose the right filter</Heading>
              <Text color="gray.600" fontSize="lg">
                The right answer depends on your water quality, your property, and your household's needs. There's no universal best option.
              </Text>
              {[
                { q: "Just want better-tasting drinking water?", a: "An under-sink filter at your kitchen tap is usually sufficient." },
                { q: "Concerned about chlorine affecting skin, hair or appliances?", a: "A whole-home system treats everything from the entry point." },
                { q: "Both concerns?", a: "They can work together. Whole-home upstream + under-sink downstream." },
                { q: "Not sure what's actually in your water?", a: "A water assessment or UNSW lab test gives you the data to decide properly." },
              ].map((item) => (
                <Box key={item.q} borderLeft={`3px solid ${WATER_BLUE}`} pl={4}>
                  <Text fontWeight="600" color={NAVY} mb={1} fontSize="sm">{item.q}</Text>
                  <Text fontSize="sm" color="gray.600">{item.a}</Text>
                </Box>
              ))}
            </VStack>
            <Box p={10} bg={ICE_MIST} borderRadius="2xl">
              <VStack spacing={6} textAlign="center">
                <Heading size="lg" color={NAVY}>Best filter starts with understanding your water.</Heading>
                <Text color="gray.600">
                  We assess before we recommend. Book a free water assessment and we'll tell you exactly what your water needs — and what it doesn't.
                </Text>
                <Button
                  as={RouterLink}
                  to="/contact"
                  bg={DEEP_AQUA}
                  color="white"
                  size="lg"
                  rightIcon={<FaArrowRight />}
                  w="full"
                  _hover={{ bg: NAVY }}
                  transition="all 0.2s"
                >
                  Book Free Assessment
                </Button>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Why One Water */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={4} mb={12} textAlign="center">
          <Heading size="xl" color={NAVY}>Why One Water Australia</Heading>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
          {[
            { title: "Assessment First", desc: "We test your water before recommending any product." },
            { title: "UNSW Lab Partnership", desc: "Independent accredited testing — not manufacturer claims." },
            { title: "Professional Installation", desc: "Correct specification, correct installation, ongoing support." },
            { title: "One Team", desc: "The same people who assess, install and maintain your system." },
          ].map((item) => (
            <Box key={item.title} p={6} bg={ICE_MIST} borderRadius="xl" textAlign="center">
              <Text fontWeight="700" color={NAVY} mb={2}>{item.title}</Text>
              <Text fontSize="sm" color="gray.600">{item.desc}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Related content */}
      <Box bg="gray.50" py={12}>
        <Container maxW="container.xl">
          <Heading size="md" color={NAVY} mb={6}>Related Reading</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {[
              { title: "Water Filtration Systems", href: "/water/water-filtration", desc: "Understand the technology: UV, carbon, reverse osmosis." },
              { title: "Water Purifiers", href: "/water/water-purifiers", desc: "When filtration isn't enough — purification systems." },
              { title: "Is Tap Water Safe to Drink?", href: "/water/blog/tap-water-safe-australia", desc: "The honest answer about Australian tap water." },
            ].map((link) => (
              <Box
                key={link.title}
                as={RouterLink}
                to={link.href}
                p={5}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                _hover={{ borderColor: WATER_BLUE }}
                transition="all 0.2s"
                display="block"
                textDecoration="none"
              >
                <Text fontWeight="600" color={NAVY} mb={1} fontSize="sm">{link.title}</Text>
                <Text fontSize="sm" color="gray.600">{link.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterFiltersHub;
